import { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, Search, X, Loader2, Plus } from "lucide-react";

const ZONE_STYLE = { color: "#171717", weight: 2, fillColor: "#171717", fillOpacity: 0.12 };

function FitZones({ zones }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 350);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (!zones.length) return;
    const b = L.latLngBounds([]);
    zones.forEach((z) => {
      if (z.geojson) b.extend(L.geoJSON(z.geojson).getBounds());
      else b.extend([z.lat, z.lon]);
    });
    if (b.isValid()) map.fitBounds(b.pad(0.15), { maxZoom: 13 });
  }, [zones, map]);
  return null;
}

function ZoneMap({ zones }) {
  return (
    <div className="h-80 rounded-xl overflow-hidden bg-neutral-100 relative z-0" data-testid="delivery-zones-map">
      <MapContainer center={[40.9168, -74.1718]} zoom={11} scrollWheelZoom className="h-full w-full">
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {zones.map((z, i) => z.geojson
          ? <GeoJSON key={`${z.label}-${i}`} data={z.geojson} style={ZONE_STYLE}><Tooltip sticky>{z.label}</Tooltip></GeoJSON>
          : <CircleMarker key={`${z.label}-${i}`} center={[z.lat, z.lon]} radius={18} pathOptions={ZONE_STYLE}><Tooltip>{z.label}</Tooltip></CircleMarker>)}
        <FitZones zones={zones} />
      </MapContainer>
    </div>
  );
}

function ZoneSearch({ q, onAdd }) {
  const [type, setType] = useState("city");
  const [text, setText] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/geo/search${q ? q + "&" : "?"}q=${encodeURIComponent(text)}&type=${type}`);
      setResults(data);
      if (!data.length) toast.error("No se encontraron resultados");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div className="inline-flex rounded-full bg-neutral-100 p-1 text-sm">
        {[["city", "Ciudad"], ["zip", "Código postal"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => { setType(v); setResults(null); }} data-testid={`zone-type-${v}`}
            className={`px-4 py-1.5 rounded-full transition-colors ${type === v ? "bg-white shadow-sm font-semibold" : "text-neutral-500"}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={search} className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="zone-search-input"
          placeholder={type === "city" ? "Ej: Paterson, NJ" : "Ej: 07501"} />
        <Button type="submit" variant="outline" disabled={busy} data-testid="zone-search-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </form>
      {results?.length > 0 && (
        <div className="rounded-xl bg-neutral-50 divide-y divide-neutral-100" data-testid="zone-search-results">
          {results.map((r, i) => (
            <button key={i} type="button" onClick={() => { onAdd(r); setResults(null); setText(""); }} data-testid={`zone-result-${i}`}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-neutral-100 transition-colors">
              <Plus className="w-4 h-4 text-neutral-400" /> {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DeliveryZones({ restaurantId }) {
  const q = restaurantId ? `?restaurant_id=${restaurantId}` : "";
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/owner/delivery-zones${q}`).then(({ data }) => setZones(data.zones || [])).catch((e) => toast.error(apiErr(e)));
  }, [q]);

  const add = (z) => {
    if (zones.some((x) => x.label === z.label)) { toast.info("Esa zona ya está agregada"); return; }
    setZones([...zones, z]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/owner/delivery-zones${q}`, { zones });
      toast.success("Zonas de delivery guardadas");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4" data-testid="delivery-zones">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-neutral-100 flex items-center justify-center"><MapPin className="w-5 h-5 text-neutral-700" /></div>
        <div>
          <h3 className="font-admin font-bold text-lg">Zonas de delivery</h3>
          <p className="text-sm text-neutral-500">Agrega las ciudades o códigos postales donde entregas. Sin zonas, se entrega en cualquier lugar.</p>
        </div>
      </div>
      <ZoneSearch q={q} onAdd={add} />
      <ZoneMap zones={zones} />
      <div className="flex flex-wrap gap-2" data-testid="zone-list">
        {zones.length === 0 && <span className="text-sm text-neutral-400">Aún no hay zonas.</span>}
        {zones.map((z, i) => (
          <span key={`${z.label}-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 pl-3 pr-1.5 py-1 text-sm" data-testid={`zone-chip-${i}`}>
            {z.label}
            <button onClick={() => setZones(zones.filter((_, j) => j !== i))} className="rounded-full p-0.5 hover:bg-neutral-200" data-testid={`zone-remove-${i}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <Button onClick={save} disabled={saving} data-testid="zones-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar zonas"}
      </Button>
    </div>
  );
}
